// frontend/src/pages/Home.js

import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ArticleCard from '../components/UI/ArticleCard';
import { motion } from 'framer-motion';

const Home = () => {
  const [allCategories, setAllCategories] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [articlesByCategory, setArticlesByCategory] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState('');

  // 1) Kategorien laden
  const fetchCategories = async () => {
    try {
      const { data } = await axiosInstance.get('/categories');
      setAllCategories(data);
    } catch {
      setError('Fehler: Kategorien konnten nicht geladen werden.');
    }
  };

  // 2) Neueste 8 Artikel für Top-Slider
  const fetchLatestArticles = async () => {
    try {
      const { data } = await axiosInstance.get('/articles/latest', {
        params: { limit: 8 },
      });
      setLatestArticles(data);
    } catch {
      setError('Fehler: Neueste Artikel konnten nicht geladen werden.');
    }
  };

  // 3) Für jede Kategorie bis zu 5 Artikel laden
  const fetchArticlesForEachCategory = async (cats) => {
    const tempObj = {};
    try {
      const promises = cats.map((c) =>
        axiosInstance.get(`/articles/category/${c.slug}`)
      );
      const results = await Promise.all(promises);
      cats.forEach((cat, idx) => {
        tempObj[cat.slug] = results[idx].data.slice(0, 5);
      });
      setArticlesByCategory(tempObj);
    } catch {
      // Fehler schlucken, da wir schon setError haben
    }
  };

  // 4) Suche aus URL übernehmen (z.B. /?search=abc)
  const handleSearchFromURL = async () => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('search');
    if (query) {
      try {
        const { data } = await axiosInstance.get('/articles/search', {
          params: { query },
        });
        setSearchResults(data);
      } catch {
        setError('Fehler: Suchanfrage konnte nicht ausgeführt werden.');
      }
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchLatestArticles();
  }, []);

  useEffect(() => {
    if (allCategories.length > 0) {
      fetchArticlesForEachCategory(allCategories);
    }
  }, [allCategories]);

  useEffect(() => {
    handleSearchFromURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.search]);

  return (
    <motion.div
      className="py-8 container mx-auto px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Slogan */}
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
          Szene isch Entfelde
        </h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto">
          Eine besondere News-Plattform, von Jugendlichen für Jugendliche. Wir
          setzen auf authentische Geschichten, lokalen Journalismus aus
          Oberentfelden und Unterentfelden sowie aktuelle Themen, um die
          nächste Generation mit frischen Perspektiven zu stärken.
        </p>
      </motion.div>

      {/* Fehleranzeige, falls vorhanden */}
      {error && (
        <div className="bg-red-100 text-red-700 p-2 mb-4">{error}</div>
      )}

      {/* Hero-Bild mit geringerer Grösse */}
      <div className="mb-10 max-w-[500px] mx-auto">
        <img
          src="/hero-section.jpeg"
          alt="Hero-Bild"
          className="w-full h-auto rounded shadow"
        />
      </div>


      {/* Wenn Suchergebnisse aus ?search= vorhanden sind */}
      {searchResults.length > 0 ? (
        <section className="mb-8">
          <h2 className="text-2xl font-heading text-primary mb-4">
            Suchergebnisse
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {searchResults.map((art) => (
              <ArticleCard key={art._id} article={art} />
            ))}
          </div>
        </section>
      ) : (
        <>
          {/* Alle Kategorien (Slider) */}
          <section className="mb-10">
            <h2 className="text-3xl font-heading text-primary mb-4">
              Alle Kategorien
            </h2>
            {allCategories.length === 0 ? (
              <p className="text-gray-600">Keine Kategorien verfügbar.</p>
            ) : (
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 3000 }}
                loop
                style={{ padding: '10px 0' }}
              >
                {allCategories.map((cat) => (
                  <SwiperSlide
                    key={cat._id}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  >
                    {/* Schwarze Buttons für Kategorien */}
                    <div className="min-w-[180px] bg-black text-white p-4 rounded shadow hover:opacity-90 transition text-center">
                      <h4 className="text-lg font-heading capitalize">
                        {cat.name}
                      </h4>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </section>

          {/* Neueste Artikel - Slider */}
          <section className="mb-10">
            <h2 className="text-3xl font-heading text-primary mb-4">
              Neueste Artikel
            </h2>
            {latestArticles.length === 0 ? (
              <p className="text-gray-600">Keine Artikel gefunden.</p>
            ) : (
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={20}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  1024: { slidesPerView: 3 },
                }}
                navigation
                pagination={{ clickable: true }}
                autoplay={{ delay: 3000 }}
                loop
              >
                {latestArticles.map((article) => (
                  <SwiperSlide
                    key={article._id}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <div className="max-w-md w-full">
                      <ArticleCard article={article} />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </section>

          {/* Artikel nach Kategorien */}
          {allCategories.map((cat) => {
            const catArticles = articlesByCategory[cat.slug] || [];
            if (catArticles.length === 0) return null;
            return (
              <section className="mb-10" key={cat._id}>
                <h2 className="text-2xl font-heading text-primary mb-4 capitalize">
                  {cat.name} - Neueste
                </h2>
                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={20}
                  slidesPerView={1}
                  breakpoints={{
                    640: { slidesPerView: 2 },
                    1024: { slidesPerView: 3 },
                  }}
                  navigation
                  pagination={{ clickable: true }}
                  autoplay={{ delay: 3000 }}
                  loop
                >
                  {catArticles.map((article) => (
                    <SwiperSlide
                      key={article._id}
                      style={{ display: 'flex', justifyContent: 'center' }}
                    >
                      <div className="max-w-md w-full">
                        <ArticleCard article={article} />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </section>
            );
          })}
        </>
      )}
    </motion.div>
  );
};

export default Home;
